import React, { useCallback, useState } from 'react';
import { ErrorMessage, SmallBodyText, SpaceWrapper } from 'ui-toolkit';
import { ActualIncomingChecksSearchTable } from '../../components/accounting/ActualIncomingChecksSearchTable';
import { ActualIncomingDetailContainer } from '../../components/accounting/ActualIncomingDetailContainer';
import { createDateRangeFromDays } from '../../utilities/accounting/createDateRangeFromDays';
import { IncomingTransactionLayout } from '../../components/accounting/IncomingTransactionLayout';
import { IEscrowActualIncomingCheckData } from '../../types/accounting/data/IEscrowActualIncomingCheckData';
import { IEscrowExpectedIncomingCheckData } from '../../types/accounting/data/IEscrowExpectedIncomingCheckData';
import { IEscrowActualIncomingCheckQuery } from '../../types/accounting/query/IEscrowActualIncomingCheckQuery';
import { IEscrowExpectedIncomingCheckQuery } from '../../types/accounting/query/IEscrowExpectedIncomingCheckQuery';
import { eMatchedQueryValue } from '../../types/accounting/eMatchedQueryValue';
import { createCheckQueryFromAmountCents } from '../../utilities/accounting/createQueryFromAmountCents';
import { ePermissions } from '../../types/administration/userAdministration/ePermissions';
import { useQueryStringQuery } from 'pages/useQueryStringQuery';
import {
  useDeleteMatchChecksIncomingMutation,
  useGetCheckIncomingActualQuery,
  useGetChecksIncomingActualQuery,
  useGetChecksIncomingExpectedQuery,
  usePostMatchChecksIncomingMutation,
} from 'API/accounting/AccountingApi';
import { createDefaultPageData } from 'types/rest/IPageData';
import { convertToError } from 'API/BaseApi';
import { createIFetchDataStateFromPieces } from './createIFetchDataStateFromPieces';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { PermissionWrapper } from '../../components/accessControl/PermissionWrapper';
import { PermissionNotification } from '../../components/accessControl/PermissionNotification';
import { useHasPermissions } from '../../components/accessControl/hooks/useHasPermissions';

const SIZE_PER_PAGE = 25;

const defaultQuery: IEscrowActualIncomingCheckQuery = {
  completedAtDate: createDateRangeFromDays(-60),
  matched: eMatchedQueryValue.UNMATCHED,
  pageNumber: 1,
  pageSize: SIZE_PER_PAGE,
  sortField: 'completedAtDate',
  sortOrder: 'desc',
  wellsFargoPaymentManagerReportType: 'PreviousDay',
};

const SelectedDisplay = (selected: IEscrowActualIncomingCheckData) => {
  const detail = JSON.stringify(selected.checkDetail ?? '');
  return (
    <SpaceWrapper leftSpace={'none'} rightSpace={'none'}>
      <SmallBodyText data-test-id={'Detail'}>Detail: {detail}</SmallBodyText>
    </SpaceWrapper>
  );
};

export const IncomingChecksPage: React.FC = () => {
  const hasActualReadPermission = useHasPermissions([ePermissions.EscrowActualIncomingChecksRead]);
  const hasExpectedReadPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingChecksRead]);
  const skip = !(hasActualReadPermission && hasExpectedReadPermission);

  const hasActualEditPermission = useHasPermissions([ePermissions.EscrowActualIncomingChecksEdit]);
  const hasExpectedEditPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingChecksEdit]);

  const { queryObject, setQueryObject } = useQueryStringQuery('checks', defaultQuery);

  const { data = createDefaultPageData(), error, isFetching } = useGetChecksIncomingActualQuery(queryObject, { skip });
  const fetchError = convertToError(error);

  const [suggestedQuery, setSuggestedQuery] = useState<IEscrowExpectedIncomingCheckQuery>(
    {} as IEscrowExpectedIncomingCheckQuery
  );

  const suggestedData = useGetChecksIncomingExpectedQuery(suggestedQuery, { skip });
  const suggestedExpected = createIFetchDataStateFromPieces({
    hookData: suggestedData,
    query: suggestedQuery,
    defaultData: createDefaultPageData<IEscrowExpectedIncomingCheckData>(),
  });

  const setSuggestedExpectedQuery = useCallback(
    (query: IEscrowExpectedIncomingCheckQuery) => setSuggestedQuery(query),
    [setSuggestedQuery]
  );
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const { data: selected } = useGetCheckIncomingActualQuery(selectedId || '', { skip: !selectedId });

  const [doMatch, { error: postError }] = usePostMatchChecksIncomingMutation();
  const onMatch = async (newMatch: IEscrowExpectedIncomingCheckData) => {
    if (!selected || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await doMatch({ actualId: selected.id, expectedId: newMatch.id });

    checkForMutationSuccess(result, 'matching check');
  };

  const [deleteMatch, { error: deleteError }] = useDeleteMatchChecksIncomingMutation();
  const onUnmatch = async (removeMatch: IEscrowExpectedIncomingCheckData) => {
    if (!selected || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await deleteMatch({ actualId: selected.id, expectedId: removeMatch.id });

    checkForMutationSuccess(result, 'unmatching check');
  };

  return (
    <PermissionWrapper
      allowedPermissions={[ePermissions.EscrowActualIncomingChecksRead, ePermissions.EscrowExpectedIncomingChecksRead]}
      requireAll={true}
    >
      <PermissionNotification
        allowedPermissions={[
          ePermissions.EscrowActualIncomingChecksEdit,
          ePermissions.EscrowExpectedIncomingChecksEdit,
        ]}
        requireAll={true}
      />
      <IncomingTransactionLayout
        table={
          <>
            <ErrorMessage
              data-test-id={'main-error-location'}
              error={fetchError || convertToError(postError) || convertToError(deleteError)}
            />
            <ActualIncomingChecksSearchTable
              isQueryFetching={isFetching}
              data={data}
              query={queryObject}
              setSelectedId={setSelectedId}
              setQuery={setQueryObject}
            />
          </>
        }
        sidecar={
          <>
            {selected && hasExpectedReadPermission && (
              <ActualIncomingDetailContainer
                createQueryFromAmountCents={createCheckQueryFromAmountCents}
                getExpectedDataFromData={(data) => data.expectedIncomingChecks || []}
                hasEditPermission={hasExpectedEditPermission && hasActualEditPermission}
                hasExpectedReadPermission={hasExpectedReadPermission}
                intro={'Select the matching deposit for this incoming check.'}
                onMatch={onMatch}
                onUnmatch={onUnmatch}
                selectedIncomingActual={selected}
                setSuggestedExpectedQuery={setSuggestedExpectedQuery}
                showDetailsOfSelected={SelectedDisplay}
                suggestedExpectedResults={suggestedExpected}
                type={'check'}
              />
            )}
          </>
        }
      />
    </PermissionWrapper>
  );
};
